// ============================================================================
// vpn/protocolFields.ts — SSoT КЛИЕНТА: состав полей профиля ПО ПРОТОКОЛУ.
//
// Сервер (VpnService/GenerateVpnClientConfig) — источник истины по генерации
// конфига; здесь — какие поля показывать/сохранять и подсказки, чтобы при смене
// протокола форма менялась осмысленно (и сохранялись ТОЛЬКО релевантные поля).
// Порты по умолчанию — ЗЕРКАЛО серверного VpnDefaultPort (подсказка UI;
// при сохранении пустой порт заполняет сервер).
// ============================================================================

export type VpnFieldTarget =
  | 'server' | 'port' | 'publicKey' | 'address' | 'dns' | 'allowedIps'
  | 'extra' | 'psk' | 'privateKey' | 'password';

export interface VpnFieldDef {
  target: VpnFieldTarget;
  /** Ключ в extra-JSON (для target='extra'). */
  extraKey?: string;
  label: string;
  hint?: string;
  type?: 'text' | 'password' | 'number' | 'textarea' | 'select';
  options?: { value: string; label: string }[];
  placeholder?: string;
}

export interface VpnProtoDef {
  code: string;
  summary: string;
  port: number;
  /**
   * Чем проверяется доступность на сервере (TestVpnProfile) — ЗЕРКАЛО
   * серверного реестра (metadata.VpnProtocolDef.Transport):
   *   'tcp' — TCP-соединение; 'ssh' — рукопожатие и вход;
   *   'udp' — проверка НЕ выполняется (UDP/QUIC: ответ не подтверждает
   *   соединение) — в форме честная подсказка, БЕЗ ложных «недоступен».
   */
  probe: 'tcp' | 'ssh' | 'udp';
  fields: VpnFieldDef[];
}

/** Порт по умолчанию (зеркало серверного VpnDefaultPort — подсказка формы). */
export const VPN_DEFAULT_PORTS: Record<string, number> = {
  wireguard: 51820, openvpn: 1194, ikev2: 500, ssh: 2222, l2tp: 1701,
  vless: 443, vmess: 443, trojan: 443, shadowsocks: 8388, hysteria2: 443, tuic: 443,
};

export function vpnDefaultPort(code: string): number {
  return VPN_DEFAULT_PORTS[code] || 0;
}

const SERVER_FIELD = (hint: string): VpnFieldDef => ({
  target: 'server', label: 'Сервер (адрес)', hint, placeholder: 'vpn.example.com',
});
const PORT_FIELD: VpnFieldDef = { target: 'port', label: 'Порт', type: 'number' };
const DNS_FIELD: VpnFieldDef = {
  target: 'dns', label: 'DNS', hint: 'Серверы имён для туннеля (через запятую)',
  placeholder: '1.1.1.1',
};
const EXTRA = (extraKey: string, label: string, extra?: Partial<VpnFieldDef>): VpnFieldDef => ({
  target: 'extra', extraKey, label, ...extra,
});

/** Реестр протоколов: поля и подсказки (порядок = порядок отображения). */
export const VPN_PROTOCOLS: VpnProtoDef[] = [
  {
    code: 'wireguard',
    summary: 'WireGuard — современный быстрый туннель. Нужны публичный ключ сервера, '
      + 'адрес клиента и (при необходимости) pre-shared key. Конфиг (.conf) и QR генерирует сервер. '
      + 'Туннель работает по UDP — серверная проверка доступности не выполняется.',
    port: 51820,
    probe: 'udp',
    fields: [
      SERVER_FIELD('Адрес сервера (endpoint) WireGuard.'),
      PORT_FIELD,
      { target: 'publicKey', label: 'Публичный ключ сервера', hint: 'Публичный ключ узла WireGuard (не приватный).' },
      { target: 'address', label: 'Адрес клиента', hint: 'IP-адрес клиента в туннеле, например 10.0.0.2/32.', placeholder: '10.0.0.2/32' },
      DNS_FIELD,
      { target: 'allowedIps', label: 'Разрешённые адреса (AllowedIPs)', hint: 'Какие адреса идут в туннель (0.0.0.0/0 — весь трафик).', placeholder: '0.0.0.0/0, ::/0' },
      { target: 'psk', label: 'Общий ключ (PSK, необязательно)', type: 'password' },
      { target: 'privateKey', label: 'Приватный ключ клиента', type: 'password', hint: 'Пусто = не менять сохранённый.' },
    ],
  },
  {
    code: 'openvpn',
    summary: 'OpenVPN — конфиг .ovpn. В «Дополнительных параметрах» задаются CA/сертификат/ключ '
      + 'и протокол (udp/tcp); логин и пароль выдаёт администратор VPN отдельно.',
    port: 1194,
    probe: 'tcp',
    fields: [
      SERVER_FIELD('Адрес OpenVPN-сервера.'),
      PORT_FIELD,
      EXTRA('proto', 'Протокол транспорта', {
        type: 'select', options: [{ value: 'udp', label: 'UDP (рекомендуется)' }, { value: 'tcp', label: 'TCP' }],
      }),
      EXTRA('cipher', 'Шифр', { placeholder: 'AES-256-GCM' }),
      EXTRA('auth', 'Хэш аутентификации', { placeholder: 'SHA256' }),
      EXTRA('ca', 'CA (сертификат центра)', { type: 'textarea', placeholder: '-----BEGIN CERTIFICATE-----' }),
      EXTRA('cert', 'Сертификат клиента', { type: 'textarea', placeholder: '-----BEGIN CERTIFICATE-----' }),
      EXTRA('key', 'Ключ клиента', { type: 'textarea', placeholder: '-----BEGIN PRIVATE KEY-----' }),
      EXTRA('tls_auth', 'TLS-auth ключ', { type: 'textarea', placeholder: '-----BEGIN OpenVPN Static key V1-----' }),
      DNS_FIELD,
    ],
  },
  {
    code: 'ikev2',
    summary: 'IKEv2 — профиль для устройств Apple (.mobileconfig). Нужны сервер, логин и пароль (PSK). '
      + 'Проверка доступности не выполняется (UDP).',
    port: 500,
    probe: 'udp',
    fields: [
      SERVER_FIELD('Адрес IKEv2-сервера.'),
      PORT_FIELD,
      EXTRA('remote_id', 'Идентификатор сервера (RemoteID)', { placeholder: 'vpn.example.com' }),
      EXTRA('username', 'Логин', { placeholder: 'vpn-user' }),
      { target: 'psk', label: 'Пароль (PSK)', type: 'password' },
      DNS_FIELD,
    ],
  },
  {
    code: 'ssh',
    summary: 'SSH — встроенный SSH-сервер платформы поднимается ЯДРОМ при активном профиле '
      + 'с протоколом «ssh» (настройки базы ssh.enabled / ssh.host / ssh.port). '
      + 'Вход — учётной записью платформы: паролем ИЛИ по ключу (устройство с открытым ключом, '
      + '«Мои устройства»). «Пароль или ключ» — если ключей нет, используйте пароль. '
      + 'Готовые команды, пробросы и хост-ключ — блок «SSH-доступ» выше.',
    port: 2222,
    probe: 'ssh',
    fields: [
      SERVER_FIELD('Адрес, по которому клиенты видят сервер (домен/IP платформы).'),
      PORT_FIELD,
      EXTRA('username', 'Логин учётной записи', {
        hint: 'Пользователь платформы, от имени которого выполняется вход (для встроенного '
          + 'сервера — ваш логин в приложении; для внешнего — логин на том сервере).',
      }),
      {
        target: 'password', label: 'Пароль учётной записи', type: 'password',
        hint: 'Пароль входа. Достаточно пароля ИЛИ ключа — если ключей нет, используйте пароль '
          + '(это рабочий вариант: ключи не всегда есть). Пусто = не менять сохранённый.',
      },
      {
        target: 'privateKey', label: 'Приватный ключ (OpenSSH/PEM)', type: 'password',
        hint: 'Приватный ключ для входа по ключу. Пусто = не менять сохранённый.',
      },
      EXTRA('host_key', 'Ожидаемый хост-ключ (пиннинг, необязательно)', {
        type: 'textarea',
        hint: 'Строка authorized_keys (ssh-ed25519 …) из блока «SSH-доступ» — защита от подмены сервера. '
          + 'Пусто — ключ принимается как при первом подключении.',
      }),
    ],
  },
  {
    code: 'l2tp',
    summary: 'L2TP/IPsec — туннель с общим ключом (PSK), логином и паролем. '
      + 'Туннель работает по UDP — серверная проверка доступности не выполняется.',
    port: 1701,
    probe: 'udp',
    fields: [
      SERVER_FIELD('Адрес L2TP-сервера.'),
      PORT_FIELD,
      EXTRA('username', 'Логин', { placeholder: 'vpn-user' }),
      { target: 'psk', label: 'Общий ключ (PSK)', type: 'password' },
      { target: 'privateKey', label: 'Пароль', type: 'password', hint: 'Пусто = не менять сохранённый.' },
      DNS_FIELD,
    ],
  },
  {
    code: 'vless',
    summary: 'VLESS (Reality) — ссылка vless://. UUID клиента — секрет; параметры Reality '
      + 'можно взять из панели (URL панели и токен — через запятую в поле панели).',
    port: 443,
    probe: 'tcp',
    fields: [
      SERVER_FIELD('Адрес VLESS-сервера (для панели 3X-UI можно не заполнять).'),
      PORT_FIELD,
      EXTRA('security', 'Безопасность', {
        type: 'select',
        options: [{ value: 'reality', label: 'Reality' }, { value: 'tls', label: 'TLS' }, { value: 'none', label: 'Без TLS' }],
      }),
      EXTRA('pbk', 'Публичный ключ Reality (pbk)'),
      EXTRA('sni', 'SNI (маскировка)', { placeholder: 'www.example.com' }),
      EXTRA('sid', 'Short ID (sid)'),
      EXTRA('flow', 'Flow', { placeholder: 'xtls-rprx-vision' }),
      EXTRA('panel_url', 'URL панели (3X-UI, необязательно)', { hint: 'Панель создаст подключение с настройками. Токен — в «Токен панели».' }),
      EXTRA('panel_token', 'Токен панели', { type: 'password' }),
      EXTRA('inbound_remark', 'Метка подключения в панели'),
      { target: 'privateKey', label: 'UUID клиента', type: 'password', hint: 'Пусто = не менять сохранённый.' },
    ],
  },
  {
    code: 'vmess',
    summary: 'VMess — ссылка vmess:// (UUID клиента + параметры транспорта).',
    port: 443,
    probe: 'tcp',
    fields: [
      SERVER_FIELD('Адрес VMess-сервера.'),
      PORT_FIELD,
      EXTRA('aid', 'Alter ID', { placeholder: '0' }),
      EXTRA('scy', 'Шифрование', { placeholder: 'auto' }),
      EXTRA('net', 'Транспорт (net)', {
        type: 'select',
        options: [{ value: 'tcp', label: 'TCP' }, { value: 'ws', label: 'WebSocket' }, { value: 'grpc', label: 'gRPC' }, { value: 'http', label: 'HTTP/2' }],
      }),
      EXTRA('type', 'Тип заголовка', { placeholder: 'none' }),
      EXTRA('host', 'Host'),
      EXTRA('path', 'Path', { placeholder: '/' }),
      EXTRA('tls', 'TLS (пусто = нет)'),
      EXTRA('sni', 'SNI'),
      { target: 'privateKey', label: 'UUID клиента', type: 'password', hint: 'Пусто = не менять сохранённый.' },
    ],
  },
  {
    code: 'trojan',
    summary: 'Trojan — пароль клиента + маскировка под HTTPS (sni/type).',
    port: 443,
    probe: 'tcp',
    fields: [
      SERVER_FIELD('Адрес Trojan-сервера.'),
      PORT_FIELD,
      EXTRA('type', 'Транспорт', {
        type: 'select',
        options: [{ value: 'tcp', label: 'TCP' }, { value: 'ws', label: 'WebSocket' }, { value: 'grpc', label: 'gRPC' }],
      }),
      EXTRA('sni', 'SNI', { placeholder: 'www.example.com' }),
      EXTRA('fp', 'Отпечаток (fp)', { placeholder: 'chrome' }),
      EXTRA('security', 'Безопасность (tls/reality)', { placeholder: 'tls' }),
      { target: 'psk', label: 'Пароль', type: 'password' },
    ],
  },
  {
    code: 'shadowsocks',
    summary: 'Shadowsocks — метод шифрования и пароль; ссылка ss://.',
    port: 8388,
    probe: 'tcp',
    fields: [
      SERVER_FIELD('Адрес Shadowsocks-сервера.'),
      PORT_FIELD,
      EXTRA('method', 'Метод шифрования', { placeholder: 'aes-256-gcm' }),
      EXTRA('plugin', 'Плагин (необязательно)'),
      { target: 'psk', label: 'Пароль', type: 'password' },
    ],
  },
  {
    code: 'hysteria2',
    summary: 'Hysteria2 — пароль и маскировка (sni/obfs). Транспорт QUIC (UDP) — проверка доступности не выполняется.',
    port: 443,
    probe: 'udp',
    fields: [
      SERVER_FIELD('Адрес Hysteria2-сервера.'),
      PORT_FIELD,
      EXTRA('sni', 'SNI', { placeholder: 'www.example.com' }),
      EXTRA('insecure', 'Не проверять сертификат (true)', { placeholder: 'false' }),
      EXTRA('obfs', 'Obfs (маскировка)', { placeholder: 'salamander' }),
      EXTRA('obfs_password', 'Пароль obfs', { type: 'password' }),
      { target: 'psk', label: 'Пароль', type: 'password' },
    ],
  },
  {
    code: 'tuic',
    summary: 'TUIC — UUID и пароль клиента, congestion control. Транспорт QUIC (UDP) — проверка доступности не выполняется.',
    port: 443,
    probe: 'udp',
    fields: [
      SERVER_FIELD('Адрес TUIC-сервера.'),
      PORT_FIELD,
      EXTRA('sni', 'SNI'),
      EXTRA('congestion_control', 'Контроль перегрузки', { placeholder: 'bbr' }),
      EXTRA('alpn', 'ALPN', { placeholder: 'h3' }),
      { target: 'privateKey', label: 'UUID клиента', type: 'password', hint: 'Пусто = не менять сохранённый.' },
      { target: 'psk', label: 'Пароль', type: 'password' },
    ],
  },
];

/** Определение протокола (неизвестный код — общий минимум: сервер+порт). */
export function vpnProtoDef(code: string): VpnProtoDef {
  return VPN_PROTOCOLS.find((p) => p.code === code)
    || { code, summary: '', port: 0, probe: 'tcp', fields: [SERVER_FIELD('Адрес сервера.'), PORT_FIELD] };
}

/** Разобрать extra-JSON профиля; не объект/битый JSON → null (форма предупредит). */
export function parseVpnExtra(json: string): Record<string, string> | null {
  if (!json || !json.trim()) return {};
  let obj: unknown;
  try {
    obj = JSON.parse(json);
  } catch {
    return null;
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    out[k] = v == null ? '' : String(v);
  }
  return out;
}

/** Сериализовать extra (пустые значения не пишутся; всё пусто → ''). */
export function serializeVpnExtra(map: Record<string, string>): string {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(map)) {
    if (v && v.trim()) out[k] = v.trim();
  }
  return Object.keys(out).length ? JSON.stringify(out) : '';
}
